/**
 * График «время — местоположение» для линейных объектов (ПЛ-27): по горизонтали — пикеты (водовод, дорога),
 * по вертикали — время сверху вниз. Работа — наклонная полоса от пикета начала в день начала до пикета
 * конца в день окончания: видно, где бригады догоняют друг друга. Без пикетов — по захваткам.
 */
import { useMemo } from 'react';
import { EmptyState } from '../../ui/feedback';
import { fmtMoment, type View } from './model';

const COLORS = ['#5f6368', '#1a73e8', '#188038', '#e37400', '#9334e6', '#c5221f', '#007b83', '#795548'];

export function LocationView({
  view,
  only,
  selectedId,
  onSelect,
  onOpen,
  height,
}: {
  view: View;
  only: Set<string> | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  height: string;
}) {
  const data = useMemo(() => {
    const acts = view.activities.filter((a) => (!only || only.has(a.id)) && a.dates);
    const byLoc = acts.filter((a) => a.locationFrom != null && a.locationTo != null);
    // Без пикетов — захватки по порядку как участки
    const zoneIdx = new Map(view.zones.map((z, i) => [z.id, i]));
    const items = (byLoc.length ? byLoc : acts.filter((a) => a.zoneId && zoneIdx.has(a.zoneId))).map((a) => {
      const z = a.zoneId ? (zoneIdx.get(a.zoneId) ?? 0) : 0;
      return {
        a,
        from: byLoc.length ? Number(a.locationFrom) : z,
        to: byLoc.length ? Number(a.locationTo) : z + 1,
        start: Date.parse(`${(a.actualStart ?? a.dates!.es).slice(0, 16)}:00Z`),
        finish: Date.parse(`${(a.actualFinish ?? a.dates!.ef).slice(0, 16)}:00Z`),
      };
    });
    return { items, byZones: !byLoc.length };
  }, [view, only]);
  if (!data.items.length)
    return (
      <EmptyState title="Нет работ с местоположением">
        Укажите в карточке работы пикеты начала и конца (метры) или захватку — и график покажет, как бригады
        идут по трассе.
      </EmptyState>
    );
  const W = 1000;
  const H = 640;
  const padL = 70;
  const padT = 30;
  const x0 = Math.min(...data.items.map((i) => Math.min(i.from, i.to)));
  const x1 = Math.max(...data.items.map((i) => Math.max(i.from, i.to)));
  const t0 = Math.min(...data.items.map((i) => i.start));
  const t1 = Math.max(...data.items.map((i) => i.finish));
  const sx = (v: number) => padL + ((v - x0) / Math.max(1, x1 - x0)) * (W - padL - 20);
  const sy = (t: number) => padT + ((t - t0) / Math.max(1, t1 - t0)) * (H - padT - 20);
  const types = [...new Set(data.items.map((i) => i.a.workType ?? ''))];
  const color = (wt: string | null) => COLORS[types.indexOf(wt ?? '') % COLORS.length]!;
  // Шкала времени — по месяцам
  const months: number[] = [];
  const d = new Date(t0);
  d.setUTCDate(1);
  d.setUTCHours(0, 0, 0, 0);
  while (d.getTime() <= t1) {
    if (d.getTime() >= t0) months.push(d.getTime());
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  const ticks = data.byZones
    ? view.zones.map((z, i) => ({ key: z.id, v: i + 0.5, label: z.name }))
    : Array.from({ length: 6 }, (_, i) => {
        const v = x0 + ((x1 - x0) * i) / 5;
        return {
          key: `pk${i}`,
          v,
          label: `ПК${Math.floor(v / 100)}+${String(Math.round(v % 100)).padStart(2, '0')}`,
        };
      });
  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-auto rounded-md border border-line bg-surface" style={{ maxHeight: height }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="min-w-[640px]"
          role="img"
          aria-label="График время — местоположение"
        >
          {months.map((m) => (
            <g key={m}>
              <line x1={padL} x2={W - 20} y1={sy(m)} y2={sy(m)} stroke="var(--color-line)" />
              <text x={4} y={sy(m) + 4} className="fill-ink-2 text-[11px]">
                {new Date(m).toLocaleDateString('ru-RU', {
                  month: 'short',
                  year: '2-digit',
                  timeZone: 'UTC',
                })}
              </text>
            </g>
          ))}
          {ticks.map((t) => (
            <g key={t.key}>
              <line
                x1={sx(t.v)}
                x2={sx(t.v)}
                y1={padT - 6}
                y2={H - 20}
                stroke="var(--color-line)"
                strokeDasharray="2 4"
              />
              <text x={sx(t.v)} y={18} textAnchor="middle" className="fill-ink-2 text-[11px]">
                {t.label}
              </text>
            </g>
          ))}
          {data.items.map((i) => {
            const sel = i.a.id === selectedId;
            const crit = i.a.dates?.critical;
            const pts = `${sx(i.from)},${sy(i.start)} ${sx(i.to)},${sy(i.finish)}`;
            return (
              // biome-ignore lint/a11y/useSemanticElements: линия на графике SVG
              <g
                key={i.a.id}
                role="button"
                tabIndex={0}
                aria-label={`${i.a.code} ${i.a.name}`}
                className="cursor-pointer outline-none"
                // Второе нажатие на выбранную работу открывает карточку — на телефоне нет двойного щелчка
                onClick={() => (sel ? onOpen(i.a.id) : onSelect(i.a.id))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onOpen(i.a.id);
                }}
              >
                <polyline points={pts} stroke="transparent" strokeWidth={44} fill="none" />
                <polyline
                  points={pts}
                  stroke={crit ? 'var(--color-danger)' : color(i.a.workType)}
                  strokeWidth={sel ? 6 : 3.5}
                  strokeLinecap="round"
                  fill="none"
                />
                <text
                  x={(sx(i.from) + sx(i.to)) / 2 + 6}
                  y={(sy(i.start) + sy(i.finish)) / 2}
                  className="fill-ink text-[11px]"
                >
                  {i.a.code}
                </text>
                <title>
                  {i.a.code} {i.a.name}: {fmtMoment(i.a.actualStart ?? i.a.dates!.es)} —{' '}
                  {fmtMoment(i.a.actualFinish ?? i.a.dates!.ef)}
                </title>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-2">
        {types.map((t) => (
          <span key={t} className="inline-flex items-center gap-1.5">
            <span className="inline-block h-1 w-5 rounded" style={{ background: color(t) }} />
            {t || 'Вид работ не указан'}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-1 w-5 rounded bg-danger" />
          критическая
        </span>
        <span>
          {data.byZones
            ? 'По захваткам: пикеты у работ не указаны.'
            : 'По горизонтали — пикеты, вниз — время.'}{' '}
          Нажмите на работу, второе нажатие откроет карточку.
        </span>
      </div>
    </div>
  );
}
